import React, { useState } from 'react';
import {
  Modal,
  Descriptions,
  Button,
  Form,
  Input,
  message,
  Typography,
  Divider,
  Space,
  Alert,
  Badge,
  Card,
  Tooltip,
} from 'antd';
import {
  UploadOutlined,
  DollarOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { TransferSchema, submitOfflinePayout } from '../../services/transferService';
import { formatDate } from '../../utils/format';
import * as filestack from 'filestack-js';

const { Title, Text } = Typography;

interface ManualPayoutDetailModalProps {
  visible: boolean;
  transfer: TransferSchema | null;
  loading: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ManualPayoutDetailModal: React.FC<ManualPayoutDetailModalProps> = ({
  visible,
  transfer,
  loading,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofUrl, setProofUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (values: { amount_sent: number }) => {
    if (!transfer || !proofUrl) {
      message.error('Please upload proof of payment');
      return;
    }

    try {
      setSubmitting(true);
      await submitOfflinePayout(transfer.id, {
        amount_sent: values.amount_sent,
        proof_of_payout_url: proofUrl,
      });
      
      message.success('Offline payout submitted successfully');
      form.resetFields();
      setProofFile(null);
      setProofUrl('');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting offline payout:', error);
      message.error('Failed to submit offline payout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = () => {
    setUploading(true);
    
    const filestackApiKey = import.meta.env.VITE_APP_FILESTACK_API_KEY;
    if (!filestackApiKey) {
      message.error('Filestack API key not configured. Please set VITE_APP_FILESTACK_API_KEY in your .env file');
      setUploading(false);
      return;
    }
    const client = filestack.init(filestackApiKey);
    
    client.picker({
      maxFiles: 1,
      maxSize: 5242880, // 5MB in bytes
      onUploadDone: (result: any) => {
        setUploading(false);
        if (result.filesUploaded && result.filesUploaded.length > 0) {
          const uploadedFile = result.filesUploaded[0];
          setProofFile(new File([], uploadedFile.filename));
          setProofUrl(uploadedFile.url);
          message.success('Proof of payment uploaded successfully');
        }
      },
      onFileUploadFailed: (file: any, error: any) => {
        setUploading(false);
        console.error('Upload failed:', error);
        message.error('Failed to upload file');
      },
      onClose: () => {
        setUploading(false);
      },
    }).open();
  };

  const handleClearUpload = () => {
    setProofFile(null);
    setProofUrl('');
    message.info('Upload cleared. You can upload a new file.');
  };

  const renderRecipientDetails = () => {
    if (!transfer?.transfer_recipient) return null;

    const recipient = transfer.transfer_recipient;
    const additionalInfo = recipient.additional_info || {};

    return (
      <>
        <Divider />
        <Descriptions title="Additional Bank Details" column={1} bordered>
          <Descriptions.Item label="Country">
            {recipient.country}
          </Descriptions.Item>
          {additionalInfo.bic_swift_code && (
            <Descriptions.Item label="BIC/SWIFT Code">
              {additionalInfo.bic_swift_code}
            </Descriptions.Item>
          )}
          {additionalInfo.bank_address && (
            <Descriptions.Item label="Bank Address">
              {additionalInfo.bank_address}
            </Descriptions.Item>
          )}
          {additionalInfo.routing_number && (
            <Descriptions.Item label="Routing Number">
              {additionalInfo.routing_number}
            </Descriptions.Item>
          )}
          {additionalInfo.sort_code && (
            <Descriptions.Item label="Sort Code">
              {additionalInfo.sort_code}
            </Descriptions.Item>
          )}
          {additionalInfo.ach_routing_number && (
            <Descriptions.Item label="ACH Routing Number">
              {additionalInfo.ach_routing_number}
            </Descriptions.Item>
          )}
          {additionalInfo.wire_routing_number && (
            <Descriptions.Item label="Wire Routing Number">
              {additionalInfo.wire_routing_number}
            </Descriptions.Item>
          )}
          {additionalInfo.beneficiary_address && (
            <Descriptions.Item label="Beneficiary Address">
              {additionalInfo.beneficiary_address}
            </Descriptions.Item>
          )}
        </Descriptions>
      </>
    );
  };

  if (!transfer) return null;

  return (
    <Modal
      title="Manual Payout Details"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Loading transfer details...</div>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <Alert
            message="Manual Payout Required"
            description="This transfer failed to process automatically. Please make a manual bank transfer and provide proof of payment."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Badge.Ribbon
            text="Manual Payout"
            color="orange"
            style={{ right: 0 }}
          >
            <Card>
              <Descriptions title="Transfer Information" column={1} bordered>
                <Descriptions.Item label="Transaction ID">
                  {transfer.transaction_id}
                </Descriptions.Item>
                <Descriptions.Item label="Reference">
                  {transfer.reference}
                </Descriptions.Item>
                <Descriptions.Item label="Amount">
                  {transfer.currency} {transfer.amount.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Purpose">
                  {transfer.purpose}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {formatDate(transfer.created)}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Descriptions title="Recipient Information" column={1} bordered>
                <Descriptions.Item label="Recipient Name">
                  {transfer.recipient_name}
                </Descriptions.Item>
                <Descriptions.Item label="Account Number">
                  {transfer.recipient_identifier}
                </Descriptions.Item>
                <Descriptions.Item label="Bank">
                  {transfer.recipient_bank}
                </Descriptions.Item>
                <Descriptions.Item label="Bank Code">
                  {transfer.recipient_bank_code}
                </Descriptions.Item>
              </Descriptions>

              {renderRecipientDetails()}
            </Card>
          </Badge.Ribbon>

          <Divider />

          <Title level={5}>Complete Manual Payout</Title>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              amount_sent: transfer.amount,
            }}
          >
            <Form.Item
              name="amount_sent"
              label="Amount Sent"
              rules={[
                { required: true, message: 'Please enter the amount sent' },
                { 
                  pattern: transfer.transfer_recipient?.supports_only_integer_amount 
                    ? /^\d+$/ 
                    : /^\d+(\.\d{1,2})?$/, 
                  message: transfer.transfer_recipient?.supports_only_integer_amount
                    ? 'This recipient only accepts whole number amounts (no decimals)'
                    : 'Please enter a valid amount' 
                }
              ]}
              help={transfer.transfer_recipient?.supports_only_integer_amount 
                ? 'Only whole numbers allowed (e.g., 100, 500, 1000)'
                : undefined
              }
            >
              <Input
                type="number"
                style={{ width: '100%' }}
                prefix={<DollarOutlined />}
                placeholder={`Enter amount in ${transfer.currency}`}
              />
            </Form.Item>

            <Form.Item
              label="Proof of Payment"
              required
            >
              <Space>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={handleFileUpload}
                  loading={uploading}
                  disabled={!!proofFile}
                >
                  {proofFile ? 'File Uploaded' : 'Upload Proof'}
                </Button>
                {proofFile && (
                  <Tooltip title="Clear upload">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleClearUpload}
                    />
                  </Tooltip>
                )}
              </Space>
              {proofFile && (
                <div style={{ marginTop: 8, color: '#52c41a' }}>
                  ✓ {proofFile.name}
                </div>
              )}
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  icon={<DollarOutlined />}
                >
                  Complete Offline Payout
                </Button>
                <Button onClick={onClose}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      )}
    </Modal>
  );
};

export default ManualPayoutDetailModal;
